import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { supabase } from '../lib/supabaseClient';

// ---------------------------------------------------------------------
//  Rol del usuario
// ---------------------------------------------------------------------
// Se lee de app_metadata, que solo la base de datos puede cambiar
// (script 11). user_metadata lo puede editar el propio usuario, así que
// se usa solo como respaldo para instalaciones sin ese script.
// La seguridad real la hace la base de datos, no esta función.
export function obtenerRol(user) {
  return user?.app_metadata?.role || user?.user_metadata?.role || null;
}

// Página de inicio según el rol: los técnicos no usan el sistema de tóner
export function rutaInicio(role) {
  return role === 'tecnico' ? '/repuestos/mi-inventario' : '/dashboard';
}

// Permisos del sistema (deben coincidir con la tabla permisos_usuario)
const PERMISOS_TONER = [
  'aprobar_registros',
  'aprobar_envios',
  'gestionar_clientes',
  'gestionar_maquinas',
  'gestionar_toners',
  'ver_historial',
];

const PERMISOS_VACIOS = {
  aprobar_registros: false,
  aprobar_envios: false,
  gestionar_clientes: false,
  gestionar_maquinas: false,
  gestionar_toners: false,
  ver_historial: false,
  gestionar_repuestos: false,
  es_tecnico: false,
};

// Trae los permisos y el nombre del usuario.
//  - Admin: todos los permisos, siempre (pero "es técnico" viene de la base).
//  - Técnico: siempre es técnico y nunca tiene permisos del tóner.
//  - Secretaria: lo que diga la tabla; nunca es técnico.
async function obtenerPerfil(userId, role) {
  let fila = null;
  try {
    const { data, error } = await supabase
      .from('permisos_usuario')
      .select('*')
      .eq('usuario_id', userId)
      .maybeSingle();
    if (!error) fila = data;
  } catch {
    // Instalación sin el script de permisos: se asume sin permisos extra
  }

  const permisos = { ...PERMISOS_VACIOS };
  for (const clave of Object.keys(PERMISOS_VACIOS)) {
    permisos[clave] = !!fila?.[clave];
  }

  if (role === 'admin') {
    for (const clave of PERMISOS_TONER) permisos[clave] = true;
    permisos.gestionar_repuestos = true;
  } else if (role === 'tecnico') {
    for (const clave of PERMISOS_TONER) permisos[clave] = false;
    permisos.es_tecnico = true;
  } else {
    permisos.es_tecnico = false;
  }

  return { permisos, nombre: fila?.nombre || null };
}

// ¿El error de Supabase significa que ya no hay sesión válida?
// (sin sesión, token vencido o revocado). Un corte de internet NO cuenta.
function sesionPerdida(error) {
  if (!error) return true; // sin usuario y sin error: no hay sesión
  if (error.name === 'AuthSessionMissingError') return true;
  return error.status === 401 || error.status === 403;
}

// Olvida al usuario guardado en el navegador (la app vuelve al login)
function limpiarSesionLocal() {
  if (useAuthStore.getState().user) {
    useAuthStore.setState({ user: null, permisos: { ...PERMISOS_VACIOS }, nombre: null });
  }
}

export const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      permisos: { ...PERMISOS_VACIOS },
      nombre: null,

      setUser: async (user) => {
        const role = obtenerRol(user);
        set({ user: { ...user, role } });
        const { permisos, nombre } = await obtenerPerfil(user?.id, role);
        set({ permisos, nombre });
      },

      logout: async () => {
        try {
          await supabase.auth.signOut();
        } catch {
          // Si la sesión ya no existía en Supabase, igual se cierra la local
        }
        set({ user: null, permisos: { ...PERMISOS_VACIOS }, nombre: null });
      },

      initializeUser: async () => {
        const { data, error } = await supabase.auth.getUser();
        const user = data?.user;
        if (user) {
          const role = obtenerRol(user);
          set({ user: { ...user, role } });
          const { permisos, nombre } = await obtenerPerfil(user.id, role);
          set({ permisos, nombre });
          return;
        }
        // Supabase no tiene una sesión válida (expiró o se cerró), pero la app
        // podía seguir "recordando" al usuario. Así todas las consultas salían
        // como anónimas y las pantallas quedaban vacías. Ahora se cierra la
        // sesión local para que vuelva a iniciar sesión.
        // (Si el problema es solo de conexión, NO se cierra la sesión.)
        if (sesionPerdida(error)) limpiarSesionLocal();
      },

      // ¿El usuario actual tiene este permiso? Los admin siempre sí.
      puede: (permiso) => {
        const { user, permisos } = get();
        if (permiso === 'es_tecnico') return get().esTecnico();
        if (user?.role === 'admin') return true;
        return !!permisos?.[permiso];
      },

      // ¿Tiene mini-inventario y registra cambios de repuestos?
      // (rol técnico, o administrador marcado como técnico)
      esTecnico: () => {
        const { user, permisos } = get();
        if (user?.role === 'tecnico') return true;
        return user?.role === 'admin' && !!permisos?.es_tecnico;
      },
    }),
    {
      name: 'auth-storage',
      getStorage: () => localStorage,
    }
  )
);

// Si Supabase cierra la sesión (token vencido o revocado), o la app abre
// sin sesión válida, se vuelve al login en vez de mostrar pantallas vacías.
supabase.auth.onAuthStateChange((evento, sesion) => {
  if (evento === 'SIGNED_OUT' || (evento === 'INITIAL_SESSION' && !sesion)) {
    limpiarSesionLocal();
  }
});